/**
 * Analytics loader for The Propertic.
 *
 * Loads two independent trackers, each behind an env var so they're
 * inert on preview builds where the keys aren't set:
 *   - Google Ads (gtag.js)  , conversion tracking for ads
 *   - Microsoft Clarity     , session replay + heatmaps
 *
 * ENV VARS (frontend/.env):
 *   REACT_APP_GOOGLE_ADS_ID                 e.g. "AW-1234567890"
 *   REACT_APP_GA_MEASUREMENT_ID             e.g. "G-XXXXXXXX"  (optional, for GA4)
 *   REACT_APP_ADS_CONVERSION_LABEL_CONSULT  e.g. "abcDEF123"   (label after the "/")
 *   REACT_APP_ADS_CONVERSION_LABEL_LOAN     e.g. "ghiJKL456"
 *   REACT_APP_MS_CLARITY_ID                 e.g. "abc123xyz"
 */

const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "";
const GA4_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";
const CLARITY_ID = process.env.REACT_APP_MS_CLARITY_ID || "";

let initialised = false;

export function initAnalytics() {
  if (initialised || typeof window === "undefined") return;
  initialised = true;

  const _isAdminPath = typeof window !== "undefined" && window.location &&
    (window.location.pathname || "").indexOf("/admin") === 0;

  // ----- Google Ads / GA4 (gtag.js) ----- never on /admin
  const gtagPrimary = ADS_ID || GA4_ID;
  if (gtagPrimary && !_isAdminPath) {
    window.__propTrackersLoaded = true;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${gtagPrimary}`;
    document.head.appendChild(s);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    if (ADS_ID) window.gtag("config", ADS_ID);
    if (GA4_ID) window.gtag("config", GA4_ID);
  }

  // ----- Microsoft Clarity -----
  // HARD-BANNED on /admin — CRM pages contain lead PII, phone numbers,
  // chat transcripts, and internal analytics. Clarity session replay
  // would leak all of it to Microsoft. Skipped even if a CLARITY_ID is
  // configured. If the user later navigates INTO /admin, `stopClarity()`
  // below ends the active session.
  if (CLARITY_ID && !_isAdminPath) {
    window.__propTrackersLoaded = true;
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", CLARITY_ID);
  }
}

/**
 * Stop the Clarity recorder + strip its cookies when the user enters
 * /admin. Called from the SPA route tracker so Clarity never sees a
 * single frame of the CRM, even if it was already loaded on a public
 * page earlier in the same session.
 */
export function stopClarity() {
  if (typeof window === "undefined") return;
  try {
    if (window.clarity) {
      try { window.clarity("stop"); } catch (_e) { /* vendor method name may change */ }
    }
    // Null out the global so any pending beacons short-circuit.
    try { window.clarity = undefined; } catch (_e) { /* ignore */ }
    // Expire Clarity's own cookies (CLID, _clck, _clsk, MR, SM, MUID).
    var names = ["_clck", "_clsk", "CLID", "MR", "SM", "MUID", "ANONCHK"];
    var expired = "; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
    for (var i = 0; i < names.length; i++) {
      document.cookie = names[i] + "=" + expired;
      document.cookie = names[i] + "=" + expired + "; domain=.clarity.ms";
      document.cookie = names[i] + "=" + expired + "; domain=.thepropertic.ae";
    }
  } catch (_e) { /* best-effort — never break CRM */ }
}

/**
 * Fire a Google Ads conversion. Safe to call even if ads aren't configured.
 *
 * @param {"consultation"|"loan"} kind
 * @param {object} [extra]  {value, currency, transaction_id}
 */
export function trackConversion(kind, extra = {}) {
  if (typeof window === "undefined" || !window.gtag || !ADS_ID) return;
  const labels = {
    consultation: process.env.REACT_APP_ADS_CONVERSION_LABEL_CONSULT || "",
    loan: process.env.REACT_APP_ADS_CONVERSION_LABEL_LOAN || "",
  };
  const label = labels[kind];
  if (!label) return;
  window.gtag("event", "conversion", {
    send_to: `${ADS_ID}/${label}`,
    value: extra.value,
    currency: extra.currency || "AED",
    transaction_id: extra.transaction_id,
  });
}

/**
 * Fire a custom event (works for GA4 + Clarity `set`).
 */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  if (window.gtag) window.gtag("event", name, params);
  if (window.clarity) window.clarity("event", name);
}

/**
 * Push an event to GTM's dataLayer. This is the primary hook the site uses
 * to fire tags configured in the Propertic GTM container
 * (Meta Lead, Google Ads Conversion, TikTok Pixel, GA4, etc.).
 *
 * Safe to call before GTM has loaded (dataLayer is bootstrapped in index.html).
 * The `language` and `page_path` fields are always attached so tags can be
 * filtered per EN / AR locale in GTM if needed.
 */
export function pushDL(event, params = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    language: (typeof document !== "undefined" && document.documentElement.lang) || "en",
    page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
    ...params,
  });
}
